import React, { useEffect, useState } from "react";
import SidePanel from "Components/DesignSystem/SidePanel/SidePanel";
import Button from "Components/DesignSystem/Button/Button";
import Status from "Components/DesignSystem/Status/Status";
import TextField from "Components/DesignSystem/TextField/TextField";
import Dropdown from "Components/DesignSystem/Dropdown/Dropdown";
import SegmentedControl from "Components/DesignSystem/SegmentedControl/SegmentedControl";
import DropdownButton from "Components/DesignSystem/DropdownButton/DropdownButton";
import Typography from "Components/DesignSystem/Typography/Typography";
import Spinner from "Components/DesignSystem/Spinner/Spinner";
import { InfoCircle } from "Components/DesignSystem/Iconography/Icons";
import { toast } from "Components/DesignSystem/Toast/useToast";
import ConfirmDialog from "Components/DesignSystem/ConfirmDialog/ConfirmDialog";
import { PROCEED } from "Pages/GMS/ARS/ARSConstants";
import AuthManager from "Common/AuthManager";
import { getRuleItem, saveRule, updateRule } from "../../service";
import {
  TConnector,
  TField,
  TIndexState,
  TProductCategory,
  TSubmodel,
  TUpsertISRProjectDataModelsInput,
} from "../../Pages/DefineModel/model";
import { TAttributeChoice } from "types/Pages/ProductDelta";
import { TDBMFieldRef, TDBMTick, TDBMDirection, TDBMRule } from "../RuleComponents/dataTypes";
import { DBM_RULE_TYPE, MULTI_CHOICE, RULE_SELECT, RULE_EMPTY_STRING, NAME, DESCRIPTION, ENTER, RULE_STAR, SUB_MODEL_TYPE, FIELDS } from "../../ISRConstants";
import DBMMatrixTable, { DBMMatrixPager } from "./DBMMatrixTable";
import DBMBulkActionModal from "./DBMBulkActionModal";
import { TDbmChoicesPayload, buildDbmDownloadFileName } from "./dbmBulkActionService";

type TDBMRuleProps = {
  selectedItem: TField | TSubmodel | TProductCategory | TConnector | null;
  userProjectDataModels: TUpsertISRProjectDataModelsInput;
  indexInfo?: TIndexState;
  editId?: string | null;
  ruleType: any;
  toggleMenu: () => void;
  /** Called after a successful create/update so the Relationships list refetches. */
  onSaved?: () => void;
};

const DEFAULT_PAGE_SIZE = 20;

// Source options are the matrix COLUMNS and Defaults options the ROWS (the GMS
// Compatibility rule's orientation) — the helper text and the download dialog
// must say it that way round (AB#15314).
export const DBM_HELPER_TEXT = "Checked = column (source) option defaults the row option. For large matrices, use Bulk Action (Upload).";

// Bulk Action › Download XLS confirmation — CBM's message layout
// (CBMCategorySelector.js: instruction line, highlighted box, "Would you like to
// proceed?", Cancel/Yes), worded for defaults (AB#15325). The marking line matches
// the workbook's own Overview sheet (`ISR/isr_dbm_bulk.py`). Not CBM's legacy
// `ConfirmModal` itself: that portals into `#modal-root` (z 5000), which sits UNDER
// this SidePanel (`z-overlay`, 5050) — it would open behind the drawer's scrim. The
// DS ConfirmDialog is on the same overlay ladder as the drawer and stacks above it.
const DOWNLOAD_CONFIRM_PRIMARY = "Defaults should be marked as x. Note that x should be in lowercase.";
const DOWNLOAD_CONFIRM_MARKING = "x = the column option pre-selects the row option. Blank = no default.";
const DOWNLOAD_CONFIRM_SECONDARY = "Please refer to the overview page of the sheet.";
const DOWNLOAD_SUCCESS = "File Exported Successfully";

const currentUserName = (): string => {
  const profile = AuthManager.getAuthUser()?.profile;
  return [profile?.first_name, profile?.last_name].filter(Boolean).join(" ");
};

// The section headings over each Submodel/Field pair (AB#15315). `caption-metadata`
// is 16px tall; with `mb-1` the heading block is 20px — the DIRECTION column's
// `pt-5` matches it so its caption still lines up with the dropdown labels.
const SECTION_HEADING_CLASS = "tw-text-ink-tertiary tw-block tw-mb-1";

const toChoices = (field: TField | null): TAttributeChoice[] =>
  field?.dataConfiguration?.choices?.map((choice) => ({
    id: choice.av_id ?? choice.id,
    value: choice.av_value ?? choice.value,
  })) ?? [];

/**
 * Breaches a single-select axis by counting ticks per row/column — mirrors the
 * backend's `find_single_select_breaches` (`ISR/isr_models.py`), which rejects
 * the same breach on every create/update and on Bulk Action. This gates Save
 * and drives the inline error banner/highlighting before a request is sent.
 */
const findBreaches = (ticks: TDBMTick[], sourceSelectType?: string, defaultsSelectType?: string): string[] => {
  const breaches: string[] = [];
  // Each select type constrains the OTHER axis — see DBMMatrixTable.tsx.
  if (defaultsSelectType === "Single-select") {
    const counts = new Map<string, number>();
    ticks.forEach((t) => counts.set(t.sourceValue, (counts.get(t.sourceValue) ?? 0) + 1));
    counts.forEach((count, value) => {
      if (count > 1) breaches.push(`Source option "${value}" has more than one default`);
    });
  }
  if (sourceSelectType === "Single-select") {
    const counts = new Map<string, number>();
    ticks.forEach((t) => counts.set(t.defaultsValue, (counts.get(t.defaultsValue) ?? 0) + 1));
    counts.forEach((count, value) => {
      if (count > 1) breaches.push(`Defaults option "${value}" has more than one default`);
    });
  }
  return breaches;
};

/**
 * The DBM Rule panel — a right-drawer (DS SidePanel, `width="2xl"`) built fresh
 * for this rule type rather than slotted into `ManageRulesDialog`'s hand-rolled
 * overlay/drawer chrome the way Math/CBM/Conditional are: it needs a badge next
 * to the title, a clamp()-based width no other panel uses. Persistence
 * is the Logic rule's: each DBM rule is its own `isr_rules` document
 * (`rule_type: "dbm"`), created/updated via `saveRule`/`updateRule` and loaded
 * for edit via `getRuleItem` — nothing is written into `userProjectDataModels`.
 * `ManageRulesDialog` renders this alone (no surrounding overlay) when
 * `ruleType === DBM`.
 */
const DBMRule = ({
  selectedItem,
  userProjectDataModels,
  indexInfo,
  editId,
  toggleMenu,
  onSaved,
}: TDBMRuleProps) => {
  const dataModels = userProjectDataModels?.project?.dataModels ?? [];
  const projectId = userProjectDataModels?.project?.id ?? undefined;

  const [name, setName] = useState<string>(RULE_EMPTY_STRING);
  const [description, setDescription] = useState<string>(RULE_EMPTY_STRING);

  const [source, setSource] = useState<TDBMFieldRef | null>(null);
  const [sourceOptions, setSourceOptions] = useState<TAttributeChoice[]>([]);

  const [defaultsSubmodel, setDefaultsSubmodel] = useState<TSubmodel | null>(null);
  const [defaultsFields, setDefaultsFields] = useState<TField[]>([]);
  const [defaultsField, setDefaultsField] = useState<TField | null>(null);
  const [defaultsOptions, setDefaultsOptions] = useState<TAttributeChoice[]>([]);

  const [direction, setDirection] = useState<TDBMDirection>("bidirectional");
  const [ticks, setTicks] = useState<TDBMTick[]>([]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [bulkModalMode, setBulkModalMode] = useState<"upload" | "validate" | null>(null);
  const [downloadConfirmOpen, setDownloadConfirmOpen] = useState<boolean>(false);
  // Edit mode fetches the rule (`getRuleItem`); Save awaits the create/update.
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  // Defaults Field candidates: the submodel's Multi-choice fields, minus Source's own field when
  // Defaults is Source's submodel — a field can't default itself, but its siblings can (2026-10-01;
  // the whole submodel used to be excluded, which also blocked every sibling field).
  const defaultsFieldCandidates = (submodel: TSubmodel | null, sourceRef: TDBMFieldRef | null): TField[] =>
    (submodel?.data.fields ?? []).filter(
      (f) => f.type === MULTI_CHOICE && !(submodel?.id === sourceRef?.submodel && f.id === sourceRef?.field),
    );

  // Source is always the field the panel was opened from, locked — same "current
  // item" lock invariant CBM/Conditional already carry (okf/isr/e2e.md).
  const initializeFromSelectedItem = () => {
    const field = selectedItem as TField;
    const dataModelIndex = indexInfo?.dataModelIndex ?? -1;
    const subModel = dataModels[dataModelIndex];
    if (!field) return;
    setSource({
      submodel: subModel?.id ?? RULE_EMPTY_STRING,
      submodelName: subModel?.data?.name ?? RULE_EMPTY_STRING,
      field: field.id ?? RULE_EMPTY_STRING,
      fieldName: field.name ?? RULE_EMPTY_STRING,
      selectType: (field.dataConfiguration?.selectType as TDBMFieldRef["selectType"]) ?? "Multi-select",
    });
    setSourceOptions(toChoices(field));
    // Panel opens blank every time a new rule is started — name/description/
    // Defaults/checked cells all reset (requirement's "Entry point" section).
    setName(RULE_EMPTY_STRING);
    setDescription(RULE_EMPTY_STRING);
    // Defaults starts unselected — the modeller picks it; nothing is pre-selected.
    setDefaultsSubmodel(null);
    setDefaultsFields([]);
    setDefaultsField(null);
    setDefaultsOptions([]);
    setDirection("bidirectional");
    setTicks([]);
    setPage(1);
  };

  // Edit mode: hydrate the panel from the rule's `isr_rules` document.
  const applyRule = (rule: TDBMRule) => {
    const body = rule.rule;
    setName(rule.name ?? RULE_EMPTY_STRING);
    setDescription(rule.description ?? RULE_EMPTY_STRING);
    setSource(body.source);
    const sourceField = dataModels
      .find((dm) => dm.id === body.source.submodel)
      ?.data.fields.find((f) => f.id === body.source.field) ?? null;
    setSourceOptions(toChoices(sourceField));

    const defaultsSubModel = dataModels.find((dm) => dm.id === body.defaults.submodel) ?? null;
    setDefaultsSubmodel(defaultsSubModel);
    // A rule saved before this guard could store defaults === source; that field is no longer a
    // candidate, so it hydrates blank and Save stays disabled until a valid Defaults Field is picked.
    const multiChoiceFields = defaultsFieldCandidates(defaultsSubModel, body.source);
    setDefaultsFields(multiChoiceFields);
    const matchedDefaultsField = multiChoiceFields.find((f) => f.id === body.defaults.field) ?? null;
    setDefaultsField(matchedDefaultsField);
    setDefaultsOptions(toChoices(matchedDefaultsField));

    setDirection(body.direction);
    setTicks(body.ticks ?? []);
    setPage(1);
  };

  useEffect(() => {
    if (!editId) {
      initializeFromSelectedItem();
      return;
    }
    // A late response for a rule the panel has since moved off must not
    // overwrite what is on screen now.
    let cancelled = false;
    setLoading(true);
    getRuleItem(editId)
      .then((res) => {
        if (cancelled) return;
        const rule = res?.data?.data as TDBMRule | undefined;
        if (rule?.rule) applyRule(rule);
        else toast.error("Rule could not be loaded");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem, editId]);

  const handleDefaultsSubmodelChange = (value: string | number) => {
    const submodel = dataModels.find((dm) => dm.id === value) ?? null;
    setDefaultsSubmodel(submodel);
    setDefaultsFields(defaultsFieldCandidates(submodel, source));
    setDefaultsField(null);
    setDefaultsOptions([]);
    setTicks([]);
    setPage(1);
  };

  const handleDefaultsFieldChange = (value: string | number) => {
    const field = defaultsFields.find((f) => f.id === value) ?? null;
    setDefaultsField(field);
    setDefaultsOptions(toChoices(field));
    // "Selecting (or changing) the Defaults Field renders the matrix and resets
    // any previously checked cells to empty" — requirement's progressive-disclosure rule.
    setTicks([]);
    setPage(1);
  };

  const handleToggleTick = (sourceValue: string, defaultsValue: string) => {
    setTicks((prev) => {
      const exists = prev.some((t) => t.sourceValue === sourceValue && t.defaultsValue === defaultsValue);
      if (exists) return prev.filter((t) => !(t.sourceValue === sourceValue && t.defaultsValue === defaultsValue));
      return [...prev, { sourceValue, defaultsValue }];
    });
  };

  // Source's select type is read LIVE from the project, like Defaults' — `source` is a snapshot
  // (`selectedItem` from the tree click, or the stored rule body), so a field switched to
  // Single-select in its config panel kept reading Multi-select here and was never checked.
  const liveSourceField = dataModels
    .find((dm) => dm.id === source?.submodel)
    ?.data.fields.find((f) => f.id === source?.field);
  const sourceSelectType: TDBMFieldRef["selectType"] =
    (liveSourceField?.dataConfiguration?.selectType as TDBMFieldRef["selectType"]) ?? source?.selectType ?? "Multi-select";
  // What is saved and exported carries it too — the backend's breach check reads the stored ref.
  const liveSource: TDBMFieldRef | null = source ? { ...source, selectType: sourceSelectType } : null;

  const breaches = findBreaches(ticks, sourceSelectType, defaultsField ? (defaultsField.dataConfiguration?.selectType as string) : undefined);
  // Defaults no longer pre-selects (Source's own submodel is excluded from its
  // list entirely) — without requiring it here too, Save would read "enabled"
  // the moment a name is typed, but silently no-op on click until a Defaults
  // Submodel/Field is actually chosen (onSave's own `!defaults` guard).
  const canSave = !!name && !!defaultsSubmodel && !!defaultsField && breaches.length === 0 && !loading && !saving;

  const buildDefaultsRef = (): TDBMFieldRef | null => {
    if (!defaultsSubmodel || !defaultsField) return null;
    return {
      submodel: defaultsSubmodel.id ?? RULE_EMPTY_STRING,
      submodelName: defaultsSubmodel.data?.name ?? RULE_EMPTY_STRING,
      field: defaultsField.id ?? RULE_EMPTY_STRING,
      fieldName: defaultsField.name ?? RULE_EMPTY_STRING,
      selectType: (defaultsField.dataConfiguration?.selectType as TDBMFieldRef["selectType"]) ?? "Multi-select",
    };
  };

  const onSave = async () => {
    if (!name) {
      toast.info("Rule name is mandatory");
      return;
    }
    const defaults = buildDefaultsRef();
    if (breaches.length > 0 || !source || !defaults) return;

    // The Logic rule's save path (SB3RuleContext's handleSaveRule/handleUpdateRule):
    // same routes, same `done`/`message` handling, same toasts.
    const dbmRule: TDBMRule = {
      name,
      description,
      rule_type: DBM_RULE_TYPE,
      project: projectId,
      rule: { source: liveSource as TDBMFieldRef, defaults, direction, ticks },
    };
    setSaving(true);
    try {
      const res = editId ? await updateRule({ ...dbmRule, id: editId }) : await saveRule(dbmRule);
      if (res?.data?.done) {
        toast.success(editId ? "Rule updated successfully" : "Rule saved successfully");
        onSaved?.();
        toggleMenu();
      } else {
        toast.error(res?.data?.message || (editId ? "Rule update unsuccessful" : "Rule save was unsuccessful"));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleBulkActionSelect = (optionId: string | number) => {
    const defaults = buildDefaultsRef();
    if (!source || !defaults) {
      toast.info("Select a Defaults field before using Bulk Action");
      return;
    }
    if (optionId === "download") {
      // Nothing downloads until the user confirms they've read how to mark the sheet.
      setDownloadConfirmOpen(true);
      return;
    }
    setBulkModalMode(optionId as "upload" | "validate");
  };

  const handleDownloadConfirm = () => {
    setDownloadConfirmOpen(false);
    const defaults = buildDefaultsRef();
    if (!source || !defaults) return;
    const fileName = buildDbmDownloadFileName({
      ruleName: name,
      ruleId: editId,
      userName: currentUserName(),
      now: new Date(),
    });
    import("./dbmBulkActionService").then(({ downloadDbmTemplate }) =>
      downloadDbmTemplate(
        {
          name: name || "dbm-rule",
          rule: { source: liveSource as TDBMFieldRef, defaults, direction, ticks },
          sourceChoices: sourceOptions,
          defaultsChoices: defaultsOptions,
        },
        fileName
      ).then((ok) => {
        if (ok) toast.success(DOWNLOAD_SUCCESS);
        else toast.error("The template could not be downloaded");
      })
    );
  };

  const sourceLabel = source ? `${source.submodelName} / ${source.fieldName}` : RULE_EMPTY_STRING;
  const defaultsLabel = defaultsSubmodel && defaultsField ? `${defaultsSubmodel.data?.name} / ${defaultsField.name}` : RULE_EMPTY_STRING;

  const bulkActionChoicesPayload: TDbmChoicesPayload | null =
    liveSource && buildDefaultsRef()
      ? { source: liveSource, defaults: buildDefaultsRef() as TDBMFieldRef, sourceChoices: sourceOptions, defaultsChoices: defaultsOptions }
      : null;

  return (
    <SidePanel
      open
      onClose={toggleMenu}
      width="2xl"
      aria-label="DBM Rule"
      // No tab row for this panel. Since design-system § 348 an omitted `tabs`
      // means no tab row too (it used to fall back to "Basic Info"/"Commend"/
      // "Guide"); kept explicit. With `tabs={[]}` the header just gets its
      // normal bottom rule (`tw-border-b`) and nothing else.
      tabs={[]}
      title={
        <div className="tw-flex tw-items-center tw-gap-2">
          <span className="tw-font-semibold">DBM Rule</span>
          <Status tone="accent" label="DEFAULT BY MATRIX" data-testid="rules-dbmrule-badge" />
        </div>
      }
      headerActions={
        <>
          <Button variant="secondary" onClick={toggleMenu} data-testid="rules-dbmrule-close-btn">
            Close
          </Button>
          <Button
            variant="primary"
            onClick={onSave}
            disabled={!canSave}
            data-testid="rules-dbmrule-save-btn"
          >
            Save
          </Button>
        </>
      }
      footer={
        !loading && defaultsField && source ? (
          <DBMMatrixPager
            totalItems={defaultsOptions.length}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          />
        ) : undefined
      }
      data-testid="rules-dbmrule-panel"
    >
      {loading ? (
        <div className="tw-flex tw-justify-center tw-py-10">
          <Spinner size="md" label="Loading rule" showLabel data-testid="rules-dbmrule-loading" />
        </div>
      ) : (
      <div className="tw-flex tw-flex-col tw-gap-4">
        <TextField
          label={NAME}
          required
          placeholder={ENTER}
          value={name}
          onChange={setName}
          data-testid="rules-dbmrule-name-input"
        />
        <TextField
          label={DESCRIPTION}
          placeholder={ENTER}
          value={description}
          onChange={setDescription}
          data-testid="rules-dbmrule-description-input"
        />

        <div className="tw-flex tw-items-start tw-gap-4">
          {/* Dropdown's own wrapper carries no width itself (`FIELD_BOX`'s `w-full` only
              fills IT) — without `flex-1 min-w-0` on each one, a bare flex row sizes
              them to their placeholder/value text instead of splitting the row evenly,
              same convention as ConditionalRuleComponent's explicit-width Dropdowns. */}
          {/* min-w-0 on the GROUP too, not just each Dropdown: a flex item's default
              `min-width: auto` is its content width, so a long Submodel/Field name grew this
              group until Direction and Defaults were pushed out of the panel. With it the
              groups split the row and the trigger's own `truncate` + `title` ellipsize the
              name at any length (2026-10-01). */}
          <div className="tw-flex-1 tw-min-w-0">
            <Typography variant="caption-metadata" className={SECTION_HEADING_CLASS} data-testid="rules-dbmrule-source-heading">
              SOURCE
            </Typography>
            <div className="tw-min-w-0 tw-flex tw-gap-2" data-testid="rules-dbmrule-source-group">
              <Dropdown
                className="tw-flex-1 tw-min-w-0"
                label={SUB_MODEL_TYPE}
                options={source ? [{ value: source.submodel, label: source.submodelName }] : []}
                value={source?.submodel}
                disabled
                placeholder={RULE_SELECT}
                onChange={() => {}}
                data-testid="rules-dbmrule-source-submodel"
              />
              <Dropdown
                className="tw-flex-1 tw-min-w-0"
                label={FIELDS}
                options={source ? [{ value: source.field, label: source.fieldName }] : []}
                value={source?.field}
                disabled
                placeholder={RULE_SELECT}
                onChange={() => {}}
                data-testid="rules-dbmrule-source-field"
              />
            </div>
          </div>

          {/* `pt-5` (20px) clears the SOURCE/DEFAULTS heading block beside it (see
              SECTION_HEADING_CLASS). Below that: FieldTitle's "title" label (Dropdown's
              default, `md`) is a 20px line-height + `mb-2` (8px) = 28px block before its
              control starts. This caption is `caption-metadata` — 16px line-height — so
              `mb-3` (12px) makes the same 28px total, lining this control up with the
              dropdowns' WITHOUT pushing "DIRECTION" itself down off their label baseline. */}
          <div className="tw-shrink-0 tw-pt-5">
            <Typography variant="caption-metadata" className="tw-text-ink-tertiary tw-text-center tw-block tw-mb-3">
              DIRECTION
            </Typography>
            {/* w-max + nowrap: without it, "Bidirectional" is the widest word and the
                segment shrinks to that in a flex row, wrapping "⇄" onto its own line
                above the text (GridListToggle's exact fix for the same collapse).
                size="md": SegmentedControl defaults to "sm" (24px); the Dropdowns
                beside it are "md" (36px) by their own default — matching the rung
                is what lines the control's height up with the fields in the row. */}
            <div className="tw-w-max tw-whitespace-nowrap">
              <SegmentedControl
                aria-label="Direction"
                size="md"
                value={direction}
                onChange={(id) => setDirection(id as TDBMDirection)}
                data-testid="rules-dbmrule-direction"
                options={[
                  { id: "forward", label: "→ Forward", "data-testid": "rules-dbmrule-direction-forward" },
                  { id: "bidirectional", label: "⇄ Bidirectional", "data-testid": "rules-dbmrule-direction-bidirectional" },
                ]}
              />
            </div>
          </div>

          <div className="tw-flex-1 tw-min-w-0">
            <Typography variant="caption-metadata" className={SECTION_HEADING_CLASS} data-testid="rules-dbmrule-defaults-heading">
              DEFAULTS
            </Typography>
            <div className="tw-min-w-0 tw-flex tw-gap-2" data-testid="rules-dbmrule-defaults-group">
              <Dropdown
                className="tw-flex-1 tw-min-w-0"
                label={SUB_MODEL_TYPE}
                /* Every submodel, Source's included — only Source's own FIELD is excluded,
                   in the Fields list (defaultsFieldCandidates). */
                options={dataModels
                  .map((dm) => ({ value: dm.id ?? RULE_EMPTY_STRING, label: dm.data?.name ?? RULE_EMPTY_STRING }))}
                value={defaultsSubmodel?.id ?? RULE_EMPTY_STRING}
                placeholder={RULE_SELECT}
                usePortal
                onChange={handleDefaultsSubmodelChange}
                data-testid="rules-dbmrule-defaults-submodel"
              />
              <Dropdown
                className="tw-flex-1 tw-min-w-0"
                label={FIELDS}
                options={defaultsFields.map((f) => ({ value: f.id ?? RULE_EMPTY_STRING, label: f.name ?? RULE_EMPTY_STRING }))}
                value={defaultsField?.id ?? RULE_EMPTY_STRING}
                placeholder={RULE_SELECT}
                disabled={!defaultsSubmodel}
                usePortal
                onChange={handleDefaultsFieldChange}
                data-testid="rules-dbmrule-defaults-field"
              />
            </div>
          </div>
        </div>

        <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
          <div className="tw-flex tw-items-center tw-gap-2 tw-text-ink-secondary tw-text-sm">
            <InfoCircle size={16} />
            <span data-testid="rules-dbmrule-helper-text">{DBM_HELPER_TEXT}</span>
          </div>
          {/* "secondary", not the toolbar-trigger "subtle" every other Bulk Action
              menu in the app uses (ProductList/BulkAction.tsx, ActionBar.tsx) — this
              one sits alone as the panel's own CTA rather than beside Sort/Filter in
              a shared toolbar row, and the design calls for the bordered box that
              only "secondary" (`tw-border tw-border-edge`) carries. */}
          <DropdownButton
            label="Bulk Action"
            variant="secondary"
            onSelect={handleBulkActionSelect}
            data-testid="rules-dbmrule-bulk-action"
            options={[
              { id: "upload", label: "Upload" },
              { id: "validate", label: "Validate" },
              { id: "download", label: "Download XLS" },
            ]}
          />
        </div>

        {breaches.length > 0 && (
          <div
            role="alert"
            className="tw-border tw-border-tone-danger-ink tw-bg-tone-danger-tint tw-rounded-srf tw-p-3"
            data-testid="rules-dbmrule-breach-banner"
          >
            <Typography variant="body-primary" className="tw-text-tone-danger-ink tw-font-medium">
              This rule can&apos;t be saved: {breaches.join("; ")}. Resolve the flagged rows/columns below before saving.
            </Typography>
          </div>
        )}

        {defaultsField && source && (
          <DBMMatrixTable
            sourceOptions={sourceOptions}
            defaultsOptions={defaultsOptions}
            ticks={ticks}
            onToggleTick={handleToggleTick}
            sourceSelectType={sourceSelectType}
            defaultsSelectType={(defaultsField.dataConfiguration?.selectType as "Single-select" | "Multi-select") ?? "Multi-select"}
            direction={direction}
            sourceLabel={sourceLabel}
            defaultsLabel={defaultsLabel}
            page={page}
            pageSize={pageSize}
          />
        )}
      </div>
      )}

      {bulkModalMode && bulkActionChoicesPayload && (
        <DBMBulkActionModal
          mode={bulkModalMode}
          choicesPayload={bulkActionChoicesPayload}
          isOpen
          onClose={() => setBulkModalMode(null)}
          onUploaded={(newTicks) => setTicks(newTicks)}
        />
      )}

      <ConfirmDialog
        open={downloadConfirmOpen}
        title="Download XLS"
        // Both slots render inside a <p>, so the highlighted box is a block <span>.
        message={
          <>
            {DOWNLOAD_CONFIRM_PRIMARY}
            <span
              className="tw-block tw-mt-2 tw-p-3 tw-rounded-srf tw-bg-tone-danger-tint tw-text-ink"
              data-testid="rules-dbmrule-download-confirm-marking"
            >
              {DOWNLOAD_CONFIRM_MARKING}
              <span className="tw-block">{DOWNLOAD_CONFIRM_SECONDARY}</span>
            </span>
          </>
        }
        secondaryMessage={PROCEED}
        confirmText="Yes"
        cancelText="Cancel"
        onConfirm={handleDownloadConfirm}
        onCancel={() => setDownloadConfirmOpen(false)}
        data-testid="rules-dbmrule-download-confirm"
      />
    </SidePanel>
  );
};

export default DBMRule;
